import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Sparkles, RotateCcw } from 'lucide-react';

/**
 * BoxFinderQuiz — kleines 5-stufiges Quiz, das die passende Collection
 * (Edition + Box-Größe) für die Kund:in vorschlägt.
 *
 * Reines Frontend-Mapping. Kein Backend-Call nötig — die Empfehlung wird
 * deterministisch aus den Antworten berechnet, sodass das Quiz auch
 * funktioniert, wenn das Netzwerk wackelt.
 */

const STEPS = [
  {
    id: 'experience',
    title: 'Ist das deine erste Box?',
    subtitle: 'Nur damit wir wissen, wie wir dich begrüßen 🤍',
    options: [
      { id: 'first', label: 'Ja, erste Box', emoji: '🎁' },
      { id: 'repeat', label: 'Ich kenne mich aus', emoji: '✨' },
    ],
  },
  {
    id: 'recipient',
    title: 'Für wen ist die Box?',
    subtitle: 'Geschenk-Modus aktiviert dann optional die Personalisierung.',
    options: [
      { id: 'self', label: 'Für mich selbst', emoji: '💖' },
      { id: 'gift', label: 'Als Geschenk', emoji: '🎀' },
    ],
  },
  {
    id: 'focus',
    title: 'Worauf liegt dein Fokus?',
    subtitle: 'Hier kannst du steuern, in welche Welt es geht.',
    options: [
      { id: 'hair', label: 'Haar-Accessoires', emoji: '🎀' },
      { id: 'beauty', label: 'Beauty & Selfcare', emoji: '💄' },
      { id: 'home', label: 'Zuhause & Wohnen', emoji: '🏡' },
      { id: 'study', label: 'Schreibtisch & Notizen', emoji: '📚' },
      { id: 'mix', label: 'Alles gemischt — überrasch mich', emoji: '🌿' },
    ],
  },
  {
    id: 'mood',
    title: 'Welche Stimmung darf es haben?',
    subtitle: 'Hilft uns, die richtige Edition zu finden.',
    options: [
      { id: 'playful', label: 'Verspielt · romantisch', emoji: '🌸' },
      { id: 'elegant', label: 'Elegant · zeitlos', emoji: '✨' },
      { id: 'cozy', label: 'Gemütlich · warm', emoji: '🍂' },
      { id: 'modern', label: 'Modern · clean', emoji: '🤍' },
      { id: 'magical', label: 'Magisch · saisonal', emoji: '❄️' },
    ],
  },
  {
    id: 'budget',
    title: 'Welches Budget?',
    subtitle: 'Damit wir die richtige Boxgröße empfehlen.',
    options: [
      { id: 'small', label: 'Bis 15 € · die kleine Überraschung', emoji: '🟣' },
      { id: 'medium', label: 'Bis 25 € · solider Mix', emoji: '🔵' },
      { id: 'large', label: 'Bis 30 € · Premium mit Statement', emoji: '👑' },
    ],
  },
];

/* ------------------------ Mapping logic ------------------------ */

// Current season → seasonal edition slug
const seasonSlug = () => {
  const m = new Date().getMonth() + 1;
  if (m >= 3 && m <= 5) return 'spring-bloom';
  if (m >= 6 && m <= 8) return 'summer-escape';
  if (m >= 9 && m <= 11) return 'autumn-mood';
  return 'winter-cozy';
};

// Mood → Hair edition slug
const HAIR_BY_MOOD = {
  playful: 'blossom-delight',
  elegant: 'golden-elegance',
  cozy: 'mocha-vintage',
  modern: 'shiny-glam',
  magical: 'shiny-glam',
};

const BEAUTY_BY_MOOD = {
  playful: 'aesthetic-beauty',
  elegant: 'soft-glow',
  cozy: 'soft-glow',
  modern: 'your-routine',
  magical: 'aesthetic-beauty',
};

const HOME_BY_MOOD = {
  playful: 'natural-living',
  elegant: 'clean-home-bath',
  cozy: 'cozy-living',
  modern: 'clean-home-bath',
  magical: 'cozy-living',
};

const STUDY_BY_MOOD = {
  playful: 'milky-pastel',
  elegant: 'noir-study',
  cozy: 'nature-study',
  modern: 'noir-study',
  magical: 'milky-pastel',
};

const FOCUS_MAP = {
  hair:   { category: 'limited-hair',     byMood: HAIR_BY_MOOD,    label: 'Hair Collection' },
  beauty: { category: 'limited-beauty',   byMood: BEAUTY_BY_MOOD,  label: 'Beauty Collection' },
  home:   { category: 'limited-home',     byMood: HOME_BY_MOOD,    label: 'Home Collection' },
  study:  { category: 'limited-study',    byMood: STUDY_BY_MOOD,   label: 'Study Box' },
};

const BUDGET_TO_SIZE = {
  small: { label: 'Small Box (≈ 14,90 €)', code: 'small' },
  medium: { label: 'Medium Box (≈ 22,90 €)', code: 'medium' },
  large: { label: 'Large Box (≈ 29,99 €)', code: 'large' },
};

function recommend(answers) {
  const focus = answers.focus;
  const mood = answers.mood || 'playful';
  const budget = answers.budget || 'small';

  // "Mix" or large budget → push seasonal as the hero
  if (focus === 'mix' || (budget === 'large' && focus !== 'hair')) {
    return {
      category: 'limited-seasonal',
      slug: seasonSlug(),
      categoryLabel: 'Limited Seasonal Collection',
      size: BUDGET_TO_SIZE[budget] || BUDGET_TO_SIZE.small,
      headline: '🌿 Deine Empfehlung: die Premium-Saisonbox',
      reason:
        'Du willst eine Mischung aus allem oder dir steht der Sinn nach etwas Besonderem — unsere Saison-Edition vereint Haar, Beauty, Home & kleine Schätze in einer Box.',
    };
  }

  const f = FOCUS_MAP[focus];
  if (!f) {
    // Fallback: seasonal
    return {
      category: 'limited-seasonal',
      slug: seasonSlug(),
      categoryLabel: 'Limited Seasonal Collection',
      size: BUDGET_TO_SIZE[budget] || BUDGET_TO_SIZE.small,
      headline: '🌿 Deine Empfehlung: die Saisonbox',
      reason: 'Wenn du dich nicht entscheiden kannst, ist die Saison-Edition immer eine schöne Wahl.',
    };
  }
  const slug = f.byMood[mood] || Object.values(f.byMood)[0];
  return {
    category: f.category,
    slug,
    categoryLabel: f.label,
    size: BUDGET_TO_SIZE[budget] || BUDGET_TO_SIZE.small,
    headline: `✨ Deine Empfehlung: ${f.label}`,
    reason: `Klare Richtung: ${f.label}. Passend zur Stimmung ${mood === 'playful' ? 'verspielt' : mood === 'elegant' ? 'elegant' : mood === 'cozy' ? 'gemütlich' : mood === 'modern' ? 'modern' : 'magisch'} schlagen wir dir diese Edition vor.`,
  };
}

/* ------------------------ Component ------------------------ */

const BoxFinderQuiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const current = STEPS[step];
  const progress = useMemo(
    () => Math.round(((done ? STEPS.length : step) / STEPS.length) * 100),
    [step, done],
  );

  const choose = (optionId) => {
    const next = { ...answers, [current.id]: optionId };
    setAnswers(next);
    if (step + 1 >= STEPS.length) {
      setDone(true);
    } else {
      setStep(step + 1);
    }
  };

  const back = () => {
    if (done) {
      setDone(false);
      return;
    }
    if (step > 0) setStep(step - 1);
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
    setDone(false);
  };

  if (done) {
    const rec = recommend(answers);
    return (
      <section
        className="relative overflow-hidden border border-[#E8DCC8] bg-white p-6 md:p-10"
        data-testid="boxfinder-result"
      >
        <div className="absolute top-4 right-4 opacity-10">
          <Sparkles className="h-20 w-20" />
        </div>
        <p className="text-[10px] tracking-[4px] uppercase text-[#C4A084] mb-2">
          ★ Quiz-Ergebnis
        </p>
        <h3
          className="text-2xl md:text-3xl font-bold text-[#5D4E3C] mb-2"
          style={{ fontFamily: 'Georgia, serif' }}
        >
          {rec.headline}
        </h3>
        <p className="text-sm md:text-base text-[#5C4E3D] max-w-2xl mb-4">
          {rec.reason}
        </p>
        <div className="flex flex-wrap items-center gap-2 mb-5 text-xs">
          <span className="px-2.5 py-1 border border-[#C4A084] text-[#5D4E3C]">
            {rec.categoryLabel}
          </span>
          <span className="px-2.5 py-1 border border-[#C4A084] text-[#5D4E3C]">
            {rec.size.label}
          </span>
        </div>

        <div className="flex flex-wrap gap-2">
          <Link
            to={`/collections/${rec.slug}`}
            className="inline-flex items-center gap-2 bg-[#5D4E3C] text-[#F5EDE4] px-5 py-2.5 text-xs tracking-[2px] uppercase font-semibold hover:bg-[#3E2723] transition-colors"
            data-testid="boxfinder-cta"
          >
            Diese Edition öffnen
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link
            to="/collections"
            state={{ activeCategory: rec.category }}
            className="inline-flex items-center gap-2 border border-[#C4A084] text-[#5D4E3C] px-5 py-2.5 text-xs tracking-[2px] uppercase font-semibold hover:bg-[#FAF5EE] transition-colors"
          >
            Alle Editionen
          </Link>
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 text-[#9C8C7A] px-3 py-2.5 text-xs tracking-[2px] uppercase font-semibold hover:text-[#5D4E3C]"
            data-testid="boxfinder-restart"
            type="button"
          >
            <RotateCcw className="h-3 w-3" />
            Nochmal
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      className="relative overflow-hidden border border-[#E8DCC8] bg-white p-6 md:p-10"
      data-testid="boxfinder-quiz"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] tracking-[4px] uppercase text-[#C4A084]">
          ★ Welche Box passt zu mir?
        </p>
        <span className="text-[10px] text-[#9C8C7A]">
          Schritt {step + 1} / {STEPS.length}
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-[3px] bg-[#F0E6D6] mb-6">
        <div
          className="h-full transition-all"
          style={{ width: `${progress}%`, background: '#C4A084' }}
        />
      </div>

      <h3
        className="text-xl md:text-2xl font-bold text-[#5D4E3C] mb-1"
        style={{ fontFamily: 'Georgia, serif' }}
      >
        {current.title}
      </h3>
      <p className="text-sm text-[#9C8C7A] mb-5">{current.subtitle}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-5">
        {current.options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => choose(opt.id)}
            className="group text-left border border-[#E8DCC8] hover:border-[#C4A084] bg-[#FAF5EE] hover:bg-white p-3.5 transition-colors flex items-center gap-3"
            data-testid={`boxfinder-option-${current.id}-${opt.id}`}
          >
            <span className="text-2xl">{opt.emoji}</span>
            <span className="text-sm text-[#5D4E3C] font-medium">{opt.label}</span>
            <ArrowRight className="h-3.5 w-3.5 ml-auto text-[#C4A084] opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        ))}
      </div>

      {step > 0 && (
        <button
          type="button"
          onClick={back}
          className="inline-flex items-center gap-1 text-xs text-[#9C8C7A] hover:text-[#5D4E3C]"
          data-testid="boxfinder-back"
        >
          <ArrowLeft className="h-3 w-3" />
          Zurück
        </button>
      )}
    </section>
  );
};

export default BoxFinderQuiz;
